import { createContext, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, clearToken, apiError } from "../api/client";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(Boolean(getToken()));

    useEffect(() => {
        const onExpired = () => setUser(null);
        window.addEventListener("auth:expired", onExpired);
        // CRITICAL: returning from Google OAuth — GoogleAuthCallback exchanges the
        // session_id first, so skip the /auth/me check here (it would 401 early).
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return () => window.removeEventListener("auth:expired", onExpired);
        }
        if (getToken()) {
            api.get("/auth/me")
                .then((r) => setUser(r.data))
                .catch(() => clearToken())
                .finally(() => setLoading(false));
        }
        return () => window.removeEventListener("auth:expired", onExpired);
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setToken(data.session_token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: apiError(e) };
        }
    };

    const signup = async (name, email, password) => {
        try {
            const { data } = await api.post("/auth/signup", { name, email, password });
            setToken(data.session_token);
            setUser(data.user);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: apiError(e) };
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const loginWithGoogle = () => {
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    // Exchange the Emergent Google OAuth session_id against the SahiKhoj app
    // backend (POST /auth/session) which returns the app's own session token.
    const exchangeGoogleSession = async (sessionId) => {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        const token = data.session_token || data.token;
        if (!token) throw new Error("No session token returned");
        setToken(token);
        const me = await api.get("/auth/me");
        setUser(me.data);
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        clearToken();
        setUser(null);
    };

    const forgotPassword = async (email) => {
        try {
            await api.post("/auth/forgot-password", { email });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: apiError(e) };
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, setUser, login, signup, loginWithGoogle, exchangeGoogleSession, logout, forgotPassword }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
